import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import router from '@/router';
import ability from "@/libs/acl/ability";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import { debounce } from "lodash";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  store.dispatch('victronenergy_sites/getAll')
  const victronenergySitesOptions = computed(() => store.getters['victronenergy_sites/getSelectedItems'])
  const victronenergySitesFilter = ref(null)

  store.dispatch('tenant/getAll')
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)

  if (router.currentRoute.params.id_site){
    victronenergySitesFilter.value = [parseInt(router.currentRoute.params.id_site)]
  }

  const tableColumns = [
    { label: "victronenergy.sites.title", key: "site.name", sortable: true },
    { label: "victronenergy.devices.name", key: "name", sortable: true },
    { label: "victronenergy.devices.product_name", key: "product_name", sortable: true },
    { label: "victronenergy.devices.product_code", key: "product_code", sortable: true },
    { label: "victronenergy.devices.machine_serial_number", key: "machine_serial_number", sortable: true },
    { label: "victronenergy.devices.firmware_version", key: "firmware_version", sortable: true },
    { label: "victronenergy.devices.last_connection", key: "last_connection", sortable: true },
    { label: "victronenergy.devices.gui_v2_status", key: "gui_v2_status", sortable: true },
    { label: "victronenergy.devices.has_victron_connect", key: "has_victron_connect", sortable: true },
    { label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("victronenergy_devices/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        id_victronenergy_site: victronenergySitesFilter.value,
      })
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([
    currentPage,
    perPage,
  ], () => {
    refetchData();
  });

  watch([searchQuery], debounce(() => { refetchData() }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      tenant: tenantFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    filtersCount,
    tenantOptions,
    tenantFilter,
    victronenergySitesOptions,
    victronenergySitesFilter,

    fetchList,
    refetchData,
    formatDateAssigned,
    buildFilters,
    clearSearchFilters,
  };
}
